/**
 * useNavigateWithSearchContext Hook
 * 
 * Custom navigation hook that automatically saves search context before
 * navigating to pricing/account pages. This ensures users don't lose their
 * search when buying credits.
 * 
 * The hook can work in two modes:
 * 1. With explicit context passed in (from components that have access to state)
 * 2. Without context - reads from "latest search" in sessionStorage (for components without state access)
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { saveSearchContext, getSearchContext } from '../utils/searchContextStorage';

interface SearchContextData {
  address?: string;
  lat?: number;
  lng?: number;
  zoneProperties?: any;
  parcelData?: any;
}

/**
 * Hook that provides navigation with automatic search context preservation
 * 
 * @returns navigateWithContext function that saves context before navigating
 */
export function useNavigateWithSearchContext() {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Navigate to a destination, optionally saving search context first
   * 
   * @param to - Destination path
   * @param searchContext - Optional search context to save
   */
  const navigateWithContext = (to: string, searchContext?: SearchContextData) => {
    console.log('🧭 Navigation intercepted:', { 
      from: location.pathname, 
      to,
      hasExplicitContext: !!searchContext
    });

    // Check if we're navigating to a pricing or account page
    const isPricingNavigation = to.includes('/pricing') || to.includes('/account/pricing');
    
    // Check if we're currently on the map page
    const isFromMap = location.pathname === '/map';

    // Try to save context if navigating to pricing from map
    if (isPricingNavigation && isFromMap) {
      // First, try to use explicitly passed context
      let contextToSave = searchContext;
      
      // If no context passed, try to read from sessionStorage (latest search)
      if (!contextToSave || !contextToSave.address) {
        console.log('⚡ No explicit context, checking for existing saved context...');
        const existingContext = getSearchContext();
        if (existingContext) {
          contextToSave = existingContext;
          console.log('✅ Found existing context in storage');
        }
      }

      // Now save if we have valid context
      if (contextToSave) {
        const { address, lat, lng, zoneProperties, parcelData } = contextToSave;
        
        // Only save if we have the essential data (address and coordinates)
        if (address && lat && lng) {
          console.log('💾 Saving search context before navigation to:', to);
          saveSearchContext(address, lat, lng, zoneProperties, parcelData);
        } else {
          console.log('⚠️ Search context incomplete, skipping save:', {
            hasAddress: !!address,
            hasLat: !!lat,
            hasLng: !!lng
          });
        }
      } else {
        console.log('ℹ️ No search context available to save');
      }
    }

    // Perform navigation
    navigate(to);
  };

  return navigateWithContext;
}

/**
 * Higher-order version that returns a wrapped navigate function
 * Useful for cases where you want to pass search context later
 */
export function useNavigateToPricingWithContext() {
  const navigateWithContext = useNavigateWithSearchContext();

  /**
   * Navigate specifically to pricing pages with context
   */
  return (pricingPath: '/pricing' | '/account/pricing', searchContext?: SearchContextData) => {
    navigateWithContext(pricingPath, searchContext);
  };
}

